import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material';

const ThemeContext = createContext({
  isDarkMode: false,
  toggleTheme: () => {},
  useSystemTheme: false,
  toggleUseSystemTheme: () => {},
});

const getSystemTheme = () => window.matchMedia('(prefers-color-scheme: dark)').matches;

export const ThemeProvider = ({ children }) => {
  const [useSystemTheme, setUseSystemTheme] = useState(() => {
    const saved = localStorage.getItem('themePreference');
    return saved ? JSON.parse(saved).useSystemTheme : true; // Default to using system theme
  });

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('themePreference');
    if (saved) {
      const prefs = JSON.parse(saved);
      return prefs.useSystemTheme ? getSystemTheme() : prefs.isDarkMode;
    }
    return getSystemTheme();
  });

  // Listen for system theme changes
  useEffect(() => {
    if (!useSystemTheme) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setIsDarkMode(e.matches);

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [useSystemTheme]);

  const toggleTheme = () => {
    if (useSystemTheme) {
      // When switching from system theme to manual, start with the current system theme
      setUseSystemTheme(false);
      setIsDarkMode(getSystemTheme());
      localStorage.setItem('themePreference', JSON.stringify({ 
        isDarkMode: getSystemTheme(), 
        useSystemTheme: false 
      }));
    } else {
      setIsDarkMode((prev) => {
        const newValue = !prev;
        localStorage.setItem('themePreference', JSON.stringify({ 
          isDarkMode: newValue, 
          useSystemTheme: false 
        }));
        return newValue;
      });
    }
  };

  const toggleUseSystemTheme = () => {
    setUseSystemTheme(prev => {
      const newValue = !prev;
      if (newValue) {
        // When switching to system theme, immediately adopt system preference
        const systemIsDark = getSystemTheme();
        setIsDarkMode(systemIsDark);
        localStorage.setItem('themePreference', JSON.stringify({ 
          isDarkMode: systemIsDark, 
          useSystemTheme: true 
        }));
      }
      return newValue;
    });
  };

  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
    },
  });

  return (
    <ThemeContext.Provider value={{ 
      isDarkMode, 
      toggleTheme, 
      useSystemTheme, 
      toggleUseSystemTheme 
    }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
